import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_COLORS = {
  1: 'bg-emerald-500',
  2: 'bg-blue-500',
  3: 'bg-amber-500',
  4: 'bg-purple-500',
  5: 'bg-pink-500',
  6: 'bg-indigo-500',
  7: 'bg-cyan-500',
  8: 'bg-orange-500',
  9: 'bg-green-600'
};

const AdminStepsDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/steps/admin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const { summary, by_step, steps_info } = dashboardData || {};

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Panel de Progreso Global</h1>
            <p className="text-navy-500">Vista general de todos los clientes por etapa</p>
          </div>
          <Button onClick={fetchDashboard} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-5 border border-navy-100 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Total Clientes Activos</p>
                <p className="text-2xl font-bold text-navy-900">{summary?.total_active_clients || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-5 border border-navy-100 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Progreso Promedio</p>
                <p className="text-2xl font-bold text-navy-900">{summary?.average_progress || 0}%</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-5 border border-navy-100 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Cerca de Completar</p>
                <p className="text-2xl font-bold text-navy-900">{summary?.close_to_completion || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-5 border border-navy-100 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-navy-500">Clientes Atascados</p>
                <p className="text-2xl font-bold text-navy-900">{summary?.stuck_clients || 0}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Steps Distribution Chart */}
        <div className="bg-white rounded-xl border border-navy-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-navy-600" />
            <h2 className="text-lg font-semibold text-navy-900">Distribución por Etapa</h2>
          </div>

          {/* Visual Bar Chart */}
          <div className="space-y-3 mb-8">
            {Object.entries(by_step || {}).map(([stepNum, data]) => {
              const maxCount = Math.max(...Object.values(by_step || {}).map(d => d.count || 0), 1);
              const percentage = ((data.count || 0) / maxCount) * 100;
              
              return (
                <div 
                  key={stepNum}
                  className={`cursor-pointer transition-all ${selectedStep === parseInt(stepNum) ? 'scale-[1.02]' : ''}`}
                  onClick={() => setSelectedStep(selectedStep === parseInt(stepNum) ? null : parseInt(stepNum))}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 text-sm font-medium text-navy-600">{stepNum}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-navy-700 truncate max-w-[200px]">
                          {data.step_name}
                        </span>
                        <span className="text-sm font-bold text-navy-900">{data.count}</span>
                      </div>
                      <div className="h-6 bg-navy-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.5, delay: parseInt(stepNum) * 0.05 }}
                          className={`h-full ${STEP_COLORS[stepNum]} rounded-full flex items-center justify-end pr-2`}
                        >
                          {data.count > 0 && (
                            <span className="text-xs text-white font-medium">
                              {data.count} cliente{data.count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </motion.div>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-navy-400 transition-transform ${selectedStep === parseInt(stepNum) ? 'rotate-90' : ''}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Step Details */}
        {selectedStep && by_step?.[selectedStep]?.clients?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-xl border border-navy-100 p-6"
          >
            <h3 className="text-lg font-semibold text-navy-900 mb-4">
              Clientes en Etapa {selectedStep}: {by_step[selectedStep].step_name}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-navy-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-navy-500">Cliente</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-navy-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-navy-500">Oficial</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-navy-500">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-navy-500">Progreso</th>
                  </tr>
                </thead>
                <tbody>
                  {by_step[selectedStep].clients.map((client, idx) => (
                    <tr key={idx} className="border-b border-navy-50 hover:bg-navy-50/50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-navy-900">{client.client_name}</span>
                      </td>
                      <td className="py-3 px-4 text-navy-600 text-sm">{client.client_email}</td>
                      <td className="py-3 px-4 text-navy-600 text-sm">{client.oficial_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          client.plan_type === 'vip' ? 'bg-amber-100 text-amber-700' :
                          client.plan_type === 'premium' ? 'bg-blue-100 text-blue-700' :
                          'bg-navy-100 text-navy-700'
                        }`}>
                          {client.plan_type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-navy-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${client.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-navy-700">{client.progress}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Steps Legend */}
        <div className="bg-white rounded-xl border border-navy-100 p-6">
          <h3 className="text-lg font-semibold text-navy-900 mb-4">Las 6 Etapas del Camino al Éxito</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(steps_info || []).map((step) => (
              <div 
                key={step.step_order}
                className="flex items-center gap-3 p-3 rounded-lg bg-navy-50/50"
              >
                <div className={`w-8 h-8 ${STEP_COLORS[step.step_order]} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                  {step.step_order}
                </div>
                <div>
                  <p className="font-medium text-navy-900 text-sm">{step.step_name}</p>
                  <p className="text-xs text-navy-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminStepsDashboard;
